/**
 * Composable for managing audit logs pagination with infinite scroll
 * @param {Object} initialParams - Initial parameters for fetching logs
 * @param {Number} pageSize - Number of items per page (default: 10)
 * @returns {Object} - { initialLoading, loadMoreLoading, hasMore, loadMore, reset }
 */
export function useAuditLogsPagination(initialParams, pageSize = 10) {
  const { getEntityAndUserLogs, resetAuditDetails } = useAuditLogsStore();

  const offset = ref(0);
  const initialLoading = ref(false);
  const loadMoreLoading = ref(false);
  const totalCount = ref(0);
  const params = ref(unref(initialParams));

  const hasMore = computed(() => {
    return (
      unref(totalCount) > unref(offset) &&
      Math.abs(unref(totalCount) - unref(offset)) > pageSize &&
      !unref(loadMoreLoading)
    );
  });

  const fetchLogs = async (isLoadMore = false) => {
    const loading = isLoadMore ? loadMoreLoading : initialLoading;

    if (unref(loading)) return;

    try {
      loading.value = true;

      const requestParams = {
        ...unref(params),
        offset: unref(offset),
      };

      const response = await getEntityAndUserLogs({ params: requestParams });

      if (response?.data?.data) {
        totalCount.value = response.data.data.count || 0;
      }
    } catch (error) {
      console.error("Error fetching audit logs:", error);

      throw error;
    } finally {
      if (isLoadMore) {
        loadMoreLoading.value = false;
      } else {
        initialLoading.value = false;
      }
    }
  };

  const loadMore = async () => {
    if (!unref(hasMore)) return;

    offset.value = unref(offset) + pageSize;

    await fetchLogs(true);
  };

  const reset = async () => {
    offset.value = 0;

    totalCount.value = 0;

    initialLoading.value = false;

    loadMoreLoading.value = false;

    await fetchLogs();
  };

  watch(initialParams, () => {
    params.value = unref(initialParams);

    reset();
  });

  // Initial fetch on mount
  onMounted(() => {
    fetchLogs();
  });

  // Cleanup on unmount
  onUnmounted(() => {
    resetAuditDetails();
  });

  return {
    initialLoading: readonly(initialLoading),
    loadMoreLoading: readonly(loadMoreLoading),
    hasMore,
    loadMore,
    reset,
  };
}
